import { useMemo, useState } from "react";
import { SendHorizontal, Bot, Zap } from "lucide-react";

export default function ComposeBox({ onSend, disabled, botEnabled, quickReplies = [], channel }) {
  const [text, setText] = useState("");
  const [sel, setSel] = useState(0);

  const slash = text.startsWith("/") && !text.includes(" ") ? text.slice(1).toLowerCase() : null;
  const matches = useMemo(
    () => (slash === null ? [] : quickReplies.filter((q) => q.shortcut.includes(slash)).slice(0, 6)),
    [slash, quickReplies],
  );

  const pick = (q) => { setText(q.text); setSel(0); };

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value || disabled) return;
    const ok = await onSend(value);
    if (ok) setText("");
  };

  const onKeyDown = (e) => {
    if (matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => (s + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => (s - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[sel] || matches[0]); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  return (
    <div className="bg-[#F0F2F5] px-4 py-2.5 shrink-0 relative">
      {matches.length > 0 && (
        <div data-testid="quick-reply-menu" className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-msg">
          <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 flex items-center gap-1"><Zap className="h-3 w-3" />Respuestas rápidas</p>
          {matches.map((q, i) => (
            <button key={q.id} data-testid={`quick-reply-option-${q.shortcut}`} onMouseDown={(e) => { e.preventDefault(); pick(q); }}
              className={`w-full text-left px-4 py-2 flex gap-3 items-baseline ${i === sel ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
              <span className="font-mono text-sm font-bold text-emerald-700">/{q.shortcut}</span>
              <span className="text-sm text-slate-600 truncate">{q.text}</span>
            </button>
          ))}
        </div>
      )}
      {botEnabled && (
        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-emerald-700" data-testid="compose-bot-hint">
          <Bot className="h-3 w-3" />El bot responde automáticamente. Si escribes tú, el chat queda asignado a ti.
        </p>
      )}
      <form onSubmit={submit} className="flex min-w-0 gap-2 items-end">
        <textarea
          data-testid="compose-input" value={text} rows={1}
          onChange={(e) => { setText(e.target.value); setSel(0); }} onKeyDown={onKeyDown}
          placeholder={channel === "simulator" ? "Escribe un mensaje (se verá en el simulador) — usa / para atajos" : "Escribe un mensaje — usa / para respuestas rápidas"}
          className="flex-1 min-w-0 min-h-[42px] max-h-36 resize-none rounded-lg bg-white px-4 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <button type="submit" data-testid="chat-send-button" disabled={!text.trim() || disabled}
          className="h-[42px] w-[42px] shrink-0 rounded-full bg-[#00A884] hover:bg-[#008f6f] text-white flex items-center justify-center disabled:opacity-40 transition-colors">
          <SendHorizontal className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
}
